import { createElement, updateCSSText } from '@syncfusion/ej2-base';
import { ContentType } from '../../models/enums';
import { normalizeRange } from './common';
import { getBlockContentElement } from './block';
import * as constants from '../../common/constant';
/**
 * Finds the closest parent element that matches the specified selector.
 *
 * @param {Node | HTMLElement} element - The starting element
 * @param {string} selector - The CSS selector to match
 * @returns {HTMLElement | null} The closest matching parent element or null if not found
 */
export function findClosestParent(element, selector) {
    if (element.nodeType === Node.TEXT_NODE) {
        return element.parentElement.closest(selector) || null;
    }
    return element.closest(selector);
}
/**
 * Gets the computed absolute position of an element relative to the document.
 *
 * @param {HTMLElement} element - The element to get the position for
 * @returns {DOMRect} The element's position and dimensions
 */
export function getElementRect(element) {
    return element.getBoundingClientRect();
}
/**
 * Checks if a node is contained within a container element.
 *
 * @param {Node} node - The node to check
 * @param {HTMLElement} container - The container element
 * @returns {boolean} True if the node is inside the container, false otherwise
 */
export function isNodeInsideElement(node, container) {
    while (node) {
        if (node === container) {
            return true;
        }
        node = node.parentNode;
    }
    return false;
}
/**
 * Renders the given string in a temp element and returns the Dom text node.
 *
 * @param {string} content - The content to render
 * @returns {Node} - Rendered dom text node
 */
export function getDomTextNode(content) {
    const temp = document.createElement('div');
    temp.innerHTML = content;
    return temp.childNodes[0];
}
/**
 * Get all text nodes within a range
 *
 * @param {Range} range - Range to get nodes from
 * @returns {Node[]} - Collection of nodes
 */
export function getNodesInRange(range) {
    const textNodes = [];
    const normalizedRange = normalizeRange(range);
    const blockEle = findClosestParent(normalizedRange.startContainer, `.${constants.BLOCK_CLS}`);
    const contentEle = getBlockContentElement(blockEle);
    const walker = document.createTreeWalker(contentEle, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            if (normalizedRange.intersectsNode(node)) {
                return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_REJECT;
        }
    });
    let node = walker.nextNode();
    while (node) {
        textNodes.push(node);
        node = walker.nextNode();
    }
    return textNodes;
}
/**
 * Creates formatting element based on the content model.
 *
 * @param {ContentModel} content - Content model.
 * @param {string | LinkData} value - Value to be set for the content.
 * @returns {Node} - Returns the formatted node.
 *
 */
export function createFormattingElement(content, value) {
    const isInlineCode = content.properties.styles.inlineCode;
    const textNode = document.createTextNode(content.content);
    const isLinkType = content.contentType === ContentType.Link;
    const styles = content.properties.styles;
    const keys = Object.keys(styles);
    // For code: preserve raw entities (&lt;, &gt;, &nbsp;, etc.)
    // For normal text: decode entities (allow &nbsp; → space)
    let formattedElement = isInlineCode ? (textNode) : (getDomTextNode(content.content) || textNode);
    // If no formatting at all (and not a link), return raw text node
    if (keys.length === 0 && !isLinkType) {
        return textNode;
    }
    if (keys.length > 0) {
        for (const styleType of keys) {
            switch (styleType.toLowerCase()) {
                case 'bold':
                    formattedElement = wrapNodeWithTag(formattedElement, 'strong');
                    break;
                case 'italic':
                    formattedElement = wrapNodeWithTag(formattedElement, 'em');
                    break;
                case 'underline':
                    formattedElement = wrapNodeWithTag(formattedElement, 'u');
                    break;
                case 'strikethrough':
                    formattedElement = wrapNodeWithTag(formattedElement, 's');
                    break;
                case 'subscript':
                    formattedElement = wrapNodeWithTag(formattedElement, 'sub');
                    break;
                case 'superscript':
                    formattedElement = wrapNodeWithTag(formattedElement, 'sup');
                    break;
                case 'inlinecode':
                    formattedElement = wrapNodeWithTag(formattedElement, 'code');
                    formattedElement.className = 'e-be-inline-code';
                    break;
                case 'color':
                case 'backgroundcolor':
                case 'uppercase':
                case 'lowercase':
                    {
                        const val = styles[styleType];
                        formattedElement = wrapNodeWithSpan(formattedElement, styleType, val);
                    }
                    break;
            }
        }
    }
    if (isLinkType) {
        const linkData = value;
        const props = content.properties;
        formattedElement = wrapNodeWithTag(formattedElement, 'a');
        if (linkData && !linkData.shouldRemoveLink) {
            formattedElement.href = props.url;
            formattedElement.target = '_blank';
            formattedElement.title = props.url;
        }
    }
    return formattedElement;
}
/**
 * Wraps a node with a specified HTML tag.
 *
 * @param {Node} node - The node to wrap
 * @param {string} tagName - The HTML tag to wrap with
 * @returns {HTMLElement} The created wrapper element
 */
export function wrapNodeWithTag(node, tagName) {
    const el = createElement(tagName);
    el.appendChild(node);
    return el;
}
/**
 * Wraps a node with a span and applies the specified style.
 *
 * @param {Node} node - The node to wrap
 * @param {string} styleType - The type of style to apply
 * @param {string | boolean} value - The style value
 * @returns {HTMLElement} The created span element
 */
export function wrapNodeWithSpan(node, styleType, value) {
    const span = createElement('span');
    switch (styleType.toLowerCase()) {
        case 'color':
            updateCSSText(span, `color: ${value};`);
            break;
        case 'backgroundcolor':
            updateCSSText(span, `background-color: ${value};`);
            break;
        case 'uppercase':
            updateCSSText(span, `text-transform: ${value ? 'uppercase' : 'none'};`);
            break;
        case 'lowercase':
            updateCSSText(span, `text-transform: ${value ? 'lowercase' : 'none'};`);
            break;
    }
    span.appendChild(node);
    return span;
}
/**
 * Removes all break tags from an HTML element.
 *
 * @param {HTMLElement} element - The element to clean
 * @returns {void}
 */
export function clearBreakTags(element) {
    element.innerHTML = element.innerHTML.replace(/<br>/g, '').trim();
}
/**
 * Checks if an element is empty (contains only whitespace or a single <br> tag).
 *
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} True if the element is empty, false otherwise
 */
export function isElementEmpty(element) {
    return element.innerText.trim() === '' || element.innerHTML === '<br>';
}
/**
 * Removes all content after the split point from the current block.
 * Handles nested structures by walking up the tree and removing siblings after each level.
 *
 * @param {Node} splitPoint - The node where split occurred (startContainer after split)
 * @param {HTMLElement} contentElement - The top-level .e-block-content element (stop boundary)
 * @returns {void}
 */
export function removeNodesAfterSplit(splitPoint, contentElement) {
    let currentNode = splitPoint;
    while (currentNode && currentNode !== contentElement) {
        const parent = currentNode.parentNode;
        // Remove currentNode and all its next siblings in this level
        let sibling = currentNode.nextSibling;
        while (sibling) {
            const nextSibling = sibling.nextSibling;
            parent.removeChild(sibling);
            sibling = nextSibling;
        }
        // Move up one level — now remove everything after this parent
        currentNode = parent;
    }
    contentElement.normalize();
}
export function getDeepestNodeAndOffset(container, offset) {
    if (container.nodeType === Node.TEXT_NODE) {
        const textNode = container;
        const len = textNode.length;
        if (offset === len) {
            // At end → try to move to next text node
            const next = textNode.nextSibling;
            if (next) {
                return { node: next, offset: 0 };
            }
            // No next → stay at end
            return { node: textNode, offset: len };
        }
        return { node: textNode, offset: Math.min(offset, len) };
    }
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let currentOffset = 0;
    let textNode = walker.nextNode();
    while (textNode) {
        const len = textNode.length;
        if (currentOffset + len > offset) {
            return { node: textNode, offset: offset - currentOffset };
        }
        currentOffset += len;
        textNode = walker.nextNode();
    }
    return null;
}
